import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { LocalStorage } from 'angular-web-storage';

import {
  loadVoucherPackFiltersFromGetParams,
  VoucherPack,
  VoucherPackFilters,
  VoucherPackStatus
} from '../../../../../../models/voucher-pack';
import { ListResponse } from '../../../../../../models/list-response';
import { AccountService } from '../../../../../../services/account/account.service';
import { VoucherPackService } from '../../../../../../services/voucher-pack/voucher-pack.service';
import { addFiltersToGetParams } from '../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';


@Component({
  selector: 'app-voucher-packs',
  templateUrl: './voucher-packs.component.html',
  styleUrls: ['./voucher-packs.component.css']
})
export class VoucherPacksComponent implements OnInit {
  public pageNumber = 1;
  @LocalStorage() public pageSizeVoucherPacks;
  public filters: VoucherPackFilters = {
    search: '',
    status: undefined
  };

  public filtersChangedSubject = new Subject<null>();

  public voucherPackList: ListResponse<VoucherPack>;
  public filtersApplied = false;

  public loadingVoucherPacks = true;

  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  public VoucherPackStatus = VoucherPackStatus;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  constructor(
    public accountService: AccountService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private voucherPackService: VoucherPackService,
    private orgService: OrganizationConfigService,
  ) {}

  ngOnInit() {
    if (!this.pageSizeVoucherPacks) {
      this.pageSizeVoucherPacks = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeVoucherPacks = this.pageSizeVoucherPacks;

    // Make browser history available to the template.
    this.history = history;

    // Handle changes to the filters by reloading the voucher packs,
    // subject to debounce.
    this.filtersChangedSubject.pipe(
      map(() => {
        this.loadingVoucherPacks = true;
        addFiltersToGetParams(
          this.filters,
          this.router,
          this.activatedRoute
        );
      }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadVoucherPacks();
    });

    // Load initial query parameters from URL if present.
    this.filters = loadVoucherPackFiltersFromGetParams(this.activatedRoute);

    this.loadVoucherPacks();
  }

  /**
   * Whether a non-zero number of results have been loaded.
   */
  public resultsPresent(): boolean {
    return (
      this.voucherPackList &&
      this.voucherPackList.results &&
      (this.voucherPackList.results.length > 0)
    );
  }

  public climberAppVoucherPackDetailsUrl(voucherPack: VoucherPack) {
    const baseUrl = this.orgService.getOrganizationConfig().climber_app_base_url;
    const productId = voucherPack.id.toString();
    return baseUrl + 'voucher-pack/purchase-init/' + productId + '/';
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.loadVoucherPacks();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.voucherPackList) { return 1; }
    return Math.ceil(this.voucherPackList.count / this.pageSizeVoucherPacks);
  }

  private loadVoucherPacks() {
    this.loadingVoucherPacks = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.voucherPackService
      .getVoucherPacks(
        this.pageNumber,
        this.pageSizeVoucherPacks,
        this.filters
      )
      .then(response => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.voucherPackList = response;

          this.filtersApplied = !!(this.filters.search || this.filters.status);
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingVoucherPacks = false;
        }
      });
  }
}
